'use client'

import type { ActionResult } from '@mentora/app-shared/i18n/action-result'
import { useActionToast } from '@mentora/app-shared/i18n/use-action-toast'
import { Button } from '@mentora/ui-components'
import { Loader2, RotateCw } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useTransition } from 'react'

export type RetryRefundAction = (input: { refundId: string }) => Promise<ActionResult<{ state: 'issued' | 'awaiting_rail' }>>

/**
 * Ask the payment provider again for a refund still waiting for it. Safe to
 * press twice: the request carries the refund's own idempotency key, so a
 * refund the provider did make is never made again.
 */
export function RetryRefundButton({ refundId, retry }: { refundId: string, retry: RetryRefundAction }) {
  const t = useTranslations('transactions.refunds')
  const { showError, showSuccess } = useActionToast()
  const [isPending, startTransition] = useTransition()

  function onClick() {
    startTransition(async () => {
      const result = await retry({ refundId })
      if (!result.ok) {
        showError(result)
        return
      }
      showSuccess(result.data?.state === 'awaiting_rail' ? t('retryStillWaiting') : t('retryIssued'))
    })
  }

  return (
    <Button variant="outline" size="sm" className="self-start" onClick={onClick} disabled={isPending}>
      {isPending ? <Loader2 className="mr-2 size-4 animate-spin" /> : <RotateCw className="mr-2 size-4" />}
      {t('retry')}
    </Button>
  )
}
